import { create } from "zustand";

/**
 * 전역 연결 집계 스토어 (설계 지침 §7-2).
 *
 * WebSocket 채널(useWebSocket)뿐 아니라 REST 폴링 건강(react-query QueryCache
 * 전역 구독)과 SW 캐시 폴백(오프라인 스냅샷) 감지도 채널로 합류한다
 * (`src/lib/rest-freshness.ts`) — WS 채널이 0개인 REST 전용 화면(Command 등)에서도
 * 백엔드 다운·stale 캐시가 ConnectionBanner 로 정직하게 노출된다.
 * - connected:    모든 채널 정상 (또는 채널 없음 / 첫 연결 시도 중)
 * - snapshot:     네트워크 대신 SW 캐시(오프라인 스냅샷) 응답을 표시 중 (§7-3)
 * - reconnecting: 하나 이상 채널이 끊겨 백오프 재시도 중
 * - lost:         재시도가 LOST_AFTER_ATTEMPTS 회 이상 누적 — 통신 두절로 승격
 *                 (백오프 1+2+4+8+16s ≈ 30초 이상 무응답)
 */
export type ChannelStatus = "connecting" | "open" | "closed" | "error";
export type ChannelKind = "ws" | "rest" | "rest-snapshot";
export type AggregateStatus = "connected" | "snapshot" | "reconnecting" | "lost";

export interface ChannelReport {
  status: ChannelStatus;
  /** 연속 재연결(WS)·연속 실패(REST) 횟수 (성공 시 0) */
  attempts: number;
  /** 마지막 메시지 수신 시각 (epoch ms) — 신선도 표시용 (§7-3).
   *  rest-snapshot 채널에서는 캐시된 응답의 Date 헤더 시각. */
  lastMessageAt: number | null;
  /** 채널 종류 — 생략 시 "ws" (기존 useWebSocket 하위호환) */
  kind?: ChannelKind;
}

interface ConnectionState {
  channels: Record<string, ChannelReport>;
  report: (id: string, report: ChannelReport) => void;
  remove: (id: string) => void;
}

/** 이 횟수 이상 연속 실패 시 "reconnecting" → "lost" 승격 */
export const LOST_AFTER_ATTEMPTS = 5;

export const useConnectionStore = create<ConnectionState>()((set) => ({
  channels: {},
  report: (id, report) =>
    set((s) => ({ channels: { ...s.channels, [id]: report } })),
  remove: (id) =>
    set((s) => {
      if (!(id in s.channels)) return s;
      const next = { ...s.channels };
      delete next[id];
      return { channels: next };
    }),
}));

/** 채널 1개의 집계 상태. connecting+attempts=0 은 "첫 연결 시도 중" — 아직 실패가
 *  아니므로 connected 취급 (페이지 전환마다 배너가 깜빡이는 오탐 방지).
 *  rest-snapshot 채널은 존재 자체가 "캐시 폴백 표시 중" 신호 → snapshot. */
export function channelAggregate(c: ChannelReport): AggregateStatus {
  if (c.kind === "rest-snapshot") return c.status === "open" ? "connected" : "snapshot";
  if (c.status === "open") return "connected";
  if (c.status === "connecting" && c.attempts === 0) return "connected";
  return c.attempts >= LOST_AFTER_ATTEMPTS ? "lost" : "reconnecting";
}

const AGGREGATE_RANK: Record<AggregateStatus, number> = {
  connected: 0,
  snapshot: 1,
  reconnecting: 2,
  lost: 3,
};

/** 파생: 전 채널 중 최악 상태 (connected < snapshot < reconnecting < lost) */
export function selectWorst(s: { channels: Record<string, ChannelReport> }): AggregateStatus {
  let worst: AggregateStatus = "connected";
  for (const c of Object.values(s.channels)) {
    const a = channelAggregate(c);
    if (a === "lost") return "lost";
    if (AGGREGATE_RANK[a] > AGGREGATE_RANK[worst]) worst = a;
  }
  return worst;
}

/** 파생: 전 채널 중 가장 최근 수신 시각 (없으면 null) */
export function selectLastMessageAt(s: { channels: Record<string, ChannelReport> }): number | null {
  let latest: number | null = null;
  for (const c of Object.values(s.channels)) {
    if (c.lastMessageAt !== null && (latest === null || c.lastMessageAt > latest)) {
      latest = c.lastMessageAt;
    }
  }
  return latest;
}

/** 파생: 오프라인 스냅샷 데이터 시각 (rest-snapshot 채널의 Date 헤더 시각, 없으면 null)
 *  — 배너 "마지막 데이터 n초/분 전" 표기용 (§7-3) */
export function selectSnapshotDataAt(s: { channels: Record<string, ChannelReport> }): number | null {
  let latest: number | null = null;
  for (const c of Object.values(s.channels)) {
    if (c.kind !== "rest-snapshot" || c.status === "open") continue;
    if (c.lastMessageAt !== null && (latest === null || c.lastMessageAt > latest)) {
      latest = c.lastMessageAt;
    }
  }
  return latest;
}

/** 파생: 끊긴 채널 중 최대 재시도 횟수 — 배너 "n회 시도" 표기용 */
export function selectMaxAttempts(s: { channels: Record<string, ChannelReport> }): number {
  let max = 0;
  for (const c of Object.values(s.channels)) {
    if (c.status !== "open" && c.attempts > max) max = c.attempts;
  }
  return max;
}

/** 훅 밖(useWebSocket 이펙트 내부)에서 쓰는 헬퍼 */
export function reportChannel(id: string, report: ChannelReport): void {
  useConnectionStore.getState().report(id, report);
}

export function removeChannel(id: string): void {
  useConnectionStore.getState().remove(id);
}
